import { useState, useEffect } from 'react';
import { setCypressFlag } from '@/lib/cypress/utils';
import { validateAndRefreshBundleDiscounts } from '@/lib/bundles/cart-bundle-validation';
import {
  updateCartAPI,
  getCartAPI,
  isBotDeniedError
} from '@/components/commerce/cart/cart-api-utils';
import { useToast } from '@/components/toast/ToastProvider';
import GenericToastContents from '@/components/toast/toast-bodies/GenericToastContents';
import Alert from '@/components/toast/icons/Alert';

/**
 * Log cart events to Datadog via API endpoint
 * @param {string} event - Event name
 * @param {Object} details - Event details
 * @param {'warn' | 'info' | 'error'} level - Log level
 */
function logCartEvent(event, details, level = 'info') {
  // Handle error message serialization like cartSecurity
  const errorMessage = details?.error?.message
    ? details.error.message
    : undefined;

  const logData = {
    message: {
      event,
      details,
      errorMessage
    },
    status: level,
    ddtags: 'cart_operations'
  };

  // Send to server-side API endpoint which forwards to Datadog
  fetch('/api/logs', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(logData)
  }).then((response) => response.json());
}

// API helper function for client-side cart operations
async function getCartFromAPI() {
  try {
    const cart = await getCartAPI();

    // Validate bundle discounts if cart has bundle items
    if (cart && cart.lines && cart.lines.length > 0) {
      const bundleItems = cart.lines.filter((item) =>
        item.attributes?.some(
          (attr) =>
            attr.key === '_bundleHandle' ||
            attr.key.includes('bundleDiscountAmount')
        )
      );

      if (bundleItems.length > 0) {
        try {
          const validation = await validateAndRefreshBundleDiscounts(cart);
          if (validation.needsRefresh && validation.fixedItems.length > 0) {
            // Update cart with fresh discount amounts
            const updatedCart = await updateCartAPI(
              cart.id,
              validation.fixedItems
            );
            if (updatedCart && !updatedCart.error) {
              // Return a completely fresh cart object to ensure React detects the change
              return { ...updatedCart, _bundleValidated: Date.now() };
            } else {
              console.error(
                'SHOPIFY CART: ERROR - API call failed:',
                updatedCart?.error || updatedCart
              );
            }
          }
        } catch (error) {
          console.error('Error validating bundle discounts:', error);
          // Return original cart if validation fails
        }
      }
    }

    return cart;
  } catch (error) {
    // Bot denials are logged as CART_ACCESS_DENIED by the caller instead.
    if (!isBotDeniedError(error)) {
      logCartEvent(
        'GET_CART_FAILED',
        {
          error: error.message,
          stack: error.stack
        },
        'error'
      );
    }
    throw error;
  }
}

/**
 * This hook loads the existing cart (if any) by checking for a cart cookie.
 * It does NOT create a cart — carts are created lazily on first add-to-cart
 * (see cart-context.js). This avoids hitting Shopify's cart-create mutation,
 * and its bot-detection check, on every page load for visitors (including
 * bots) who never add anything to their cart.
 */
export default function useShopifyCart() {
  const [cart, setCart] = useState(null);
  const [cartReady, setCartReady] = useState(false);
  const { addToast } = useToast();

  // Settles once the initial lookup finishes (cart or null) so add-to-cart can
  // wait for it instead of creating a replacement cart.
  const [initialCartDeferred] = useState(() => {
    let resolve;
    const promise = new Promise((r) => {
      resolve = r;
    });
    return { promise, resolve };
  });

  useEffect(() => {
    const getInitialCart = async () => {
      let initialCart = null;
      try {
        initialCart = await getCartFromAPI();
        setCart(initialCart);
      } catch (error) {
        if (isBotDeniedError(error)) {
          console.warn('Cart access denied by bot check:', error);
          logCartEvent(
            'CART_ACCESS_DENIED',
            {
              error: error.message
            },
            'warn'
          );
          addToast(
            <GenericToastContents title="Access Restricted" Icon={Alert}>
              Cart access was blocked for security reasons, which may affect how
              the site works for you right now. Please refresh the page or chat
              with our Comfort Crew if this continues.
            </GenericToastContents>,
            { type: 'warning', timer: null }
          );
          return;
        }

        console.error('Error fetching initial cart:', error);
        logCartEvent(
          'CART_INITIALIZATION_ERROR',
          {
            error
          },
          'warn'
        );
        // Leave cart as null; a cart will be lazily created on the next
        // add-to-cart attempt instead of eagerly creating one here.
      } finally {
        setCartReady(true);
        setCypressFlag('cartReady', true);
        initialCartDeferred.resolve(initialCart);
      }
    };
    getInitialCart();
  }, [addToast, initialCartDeferred]);

  return [cart, setCart, cartReady, initialCartDeferred.promise];
}
